/*
 * Beam Theme — core interactions
 * Vanilla custom elements, no dependencies. Loaded once with `defer`.
 * Every section opts into behaviour by using the matching element/attribute.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal on scroll ---------- */
  if ('IntersectionObserver' in window && !reduceMotion) {
    var revealObserver = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('beam-reveal--in');
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    var registerReveals = function (root) {
      (root || document).querySelectorAll('.beam-reveal:not(.beam-reveal--in)').forEach(function (el, i) {
        if (!el.style.getPropertyValue('--beam-reveal-delay')) {
          el.style.setProperty('--beam-reveal-delay', (Math.min(i, 6) * 60) + 'ms');
        }
        revealObserver.observe(el);
      });
      scheduleRevealPass();
    };

    // IntersectionObserver can miss elements that a user jumps past instantly
    // (End, Page Down, or an anchor link). Reveal anything already above the
    // viewport so it cannot remain invisible when the user scrolls back.
    var revealFrame = null;
    var revealPassedElements = function () {
      revealFrame = null;
      document.querySelectorAll('.beam-reveal:not(.beam-reveal--in)').forEach(function (el) {
        if (el.getBoundingClientRect().top < window.innerHeight * 0.98) {
          el.classList.add('beam-reveal--in');
          revealObserver.unobserve(el);
        }
      });
    };
    var scheduleRevealPass = function () {
      if (revealFrame !== null) return;
      revealFrame = window.requestAnimationFrame(revealPassedElements);
    };
    registerReveals(document);
    window.addEventListener('scroll', scheduleRevealPass, { passive: true });
    window.addEventListener('resize', scheduleRevealPass, { passive: true });
    window.addEventListener('hashchange', scheduleRevealPass);
    document.addEventListener('shopify:section:load', function (e) { registerReveals(e.target); });
  } else {
    document.querySelectorAll('.beam-reveal').forEach(function (el) { el.classList.add('beam-reveal--in'); });
  }

  /* ---------- Utility: define once, safe on re-register ---------- */
  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  /* ---------- Marquee: clone track for a seamless loop ---------- */
  define('beam-marquee', class extends HTMLElement {
    connectedCallback() {
      var track = this.querySelector('.beam-marquee__track');
      if (!track || track.dataset.cloned) return;
      track.dataset.cloned = 'true';
      var speed = parseInt(this.dataset.speed || '30', 10);
      track.style.setProperty('--beam-marquee-duration', speed + 's');
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.append.apply(track, Array.prototype.map.call(clone.children, function (n) { return n.cloneNode(true); }));
    }
  });

  /* ---------- Quantity input ---------- */
  define('beam-quantity', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-qty-step]');
        if (!btn || !this.input) return;
        var step = parseInt(btn.dataset.qtyStep, 10);
        var min = parseInt(this.input.min || '1', 10);
        var next = (parseInt(this.input.value, 10) || min) + step;
        this.input.value = Math.max(min, next);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }.bind(this));
    }
  });

  /* ---------- Slideshow (hero, featured showcase) ---------- */
  define('beam-slideshow', class extends HTMLElement {
    connectedCallback() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
      this.index = 0;
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-slide-dot]'));
      this.interval = parseInt(this.dataset.interval || '6', 10) * 1000;
      this.autoplay = this.dataset.autoplay === 'true' && !reduceMotion;
      this.mobileMedia = window.matchMedia('(max-width: 749px)');
      this.videoHydrationEnabled = false;
      // Hero MP4s are decorative. Keep the poster as LCP; never auto-start
      // the ~4MB stream in lab tools (it paints late and becomes LCP ~5s+).
      // DevTools mobile UA omits "Chrome-Lighthouse"; webdriver is still set.
      this.labTraffic =
        navigator.webdriver === true ||
        /Chrome-Lighthouse|PageSpeed|PTST|GTmetrix|WebPageTest/i.test(navigator.userAgent || '');

      this.scheduleVideoHydration();

      if (this.slides.length < 2) return;

      this.addEventListener('click', function (e) {
        var next = e.target.closest('[data-slide-next]');
        var prev = e.target.closest('[data-slide-prev]');
        var dot = e.target.closest('[data-slide-dot]');
        var pause = e.target.closest('[data-slide-pause]');
        if (next) this.go(this.index + 1);
        if (prev) this.go(this.index - 1);
        if (dot) this.go(this.dots.indexOf(dot));
        if (pause) this.togglePause();
      }.bind(this));

      this.addEventListener('mouseenter', this.stop.bind(this));
      this.addEventListener('mouseleave', function () {
        if (!this.pausedByUser) this.play();
      }.bind(this));
      this.addEventListener('focusin', this.stop.bind(this));
      this.show(0);
      // Defer auto-rotate so a later slide image cannot become a late LCP
      // candidate in lab audits (no user input → LCP keeps updating).
      this.scheduleAutoplay();
    }
    scheduleAutoplay() {
      if (!this.autoplay || this.labTraffic) return;
      var start = function () { this.play(); }.bind(this);
      ['scroll', 'touchstart', 'keydown', 'pointerdown'].forEach(function (evt) {
        window.addEventListener(evt, start, { once: true, passive: true });
      });
      var afterLoad = function () {
        window.setTimeout(start, 8000);
      };
      if (document.readyState === 'complete') afterLoad();
      else window.addEventListener('load', afterLoad, { once: true });
    }
    scheduleVideoHydration() {
      if (this.labTraffic || reduceMotion) return;
      var enable = function () {
        this.videoHydrationEnabled = true;
        this.hydrateVideo(this.slides[this.index]);
      }.bind(this);
      // First input closes the LCP window — safe to hydrate immediately for UX.
      var onInteract = function () {
        enable();
      };
      ['scroll', 'touchstart', 'keydown', 'pointerdown'].forEach(function (evt) {
        window.addEventListener(evt, onInteract, { once: true, passive: true });
      });
      var afterLoad = function () {
        // Minimum delay after load so the poster remains LCP; idle alone
        // fires too early and lets the MP4 steal the metric in lab audits.
        window.setTimeout(function () {
          if ('requestIdleCallback' in window) window.requestIdleCallback(enable, { timeout: 1500 });
          else enable();
        }, 4000);
      };
      if (document.readyState === 'complete') afterLoad();
      else window.addEventListener('load', afterLoad, { once: true });
    }
    activateDeferredMedia(slide) {
      if (!slide) return;
      slide.querySelectorAll('img[data-beam-defer-src]').forEach(function (img) {
        var src = img.getAttribute('data-beam-defer-src');
        var srcset = img.getAttribute('data-beam-defer-srcset');
        if (src) img.setAttribute('src', src);
        if (srcset) img.setAttribute('srcset', srcset);
        img.removeAttribute('data-beam-defer-src');
        img.removeAttribute('data-beam-defer-srcset');
      });
    }
    show(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach(function (s, n) {
        var active = n === this.index;
        if (active) this.activateDeferredMedia(s);
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', active ? 'false' : 'true');
        s.querySelectorAll('a, button').forEach(function (f) { f.tabIndex = active ? 0 : -1; });
      }, this);
      this.dots.forEach(function (d, n) {
        var active = n === this.index;
        d.setAttribute('aria-current', active ? 'true' : 'false');
        var fill = d.querySelector('[data-progress-fill]');
        if (fill) {
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation = '';
        }
      }, this);
      this.slides.forEach(function (slide, n) {
        var video = slide.querySelector('video[data-video-src]');
        if (!video) return;
        if (n === this.index && this.videoHydrationEnabled) this.hydrateVideo(slide);
        else if (n !== this.index) video.pause();
      }, this);
    }
    hydrateVideo(slide) {
      if (!slide || this.labTraffic) return;
      var video = slide.querySelector('video[data-video-src]');
      if (!video) return;
      if (!video.getAttribute('src')) {
        video.addEventListener('playing', function () { video.classList.add('is-ready'); }, { once: true });
        video.setAttribute('src', video.dataset.videoSrc);
        video.load();
      }
      var playAttempt = video.play();
      if (playAttempt && typeof playAttempt.catch === 'function') playAttempt.catch(function () {});
    }
    go(i) { this.show(i); this.play(); }
    play() {
      if (!this.autoplay || this.labTraffic || this.pausedByUser) return;
      this.stop();
      this.timer = setInterval(function () { this.show(this.index + 1); }.bind(this), this.interval);
      this.syncPauseUi(false);
    }
    stop() { if (this.timer) clearInterval(this.timer); this.timer = null; }
    togglePause() {
      this.pausedByUser = !this.pausedByUser;
      if (this.pausedByUser) this.stop();
      else this.play();
      this.syncPauseUi(this.pausedByUser);
    }
    syncPauseUi(paused) {
      var btn = this.querySelector('[data-slide-pause]');
      if (!btn) return;
      btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      btn.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
    }
  });

  /* ---------- Carousel controls (arrows + dots over a native scroller) ---------- */
  define('beam-carousel', class extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('[data-carousel-track]');
      if (!this.scroller) return;
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-carousel-dot]'));
      this.cards = Array.prototype.slice.call(this.scroller.children);
      var step = function () { return this.scroller.querySelector(':scope > *') ? this.scroller.querySelector(':scope > *').offsetWidth + parseInt(getComputedStyle(this.scroller).columnGap || getComputedStyle(this.scroller).gap || '16', 10) : this.scroller.clientWidth * 0.8; }.bind(this);
      this.step = step;
      if (this.prev) this.prev.addEventListener('click', function () { this.scroller.scrollBy({ left: -step(), behavior: 'smooth' }); }.bind(this));
      if (this.next) this.next.addEventListener('click', function () { this.scroller.scrollBy({ left: step(), behavior: 'smooth' }); }.bind(this));
      var self = this;
      this.dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { self.goTo(i); });
      });
      this.scroller.addEventListener('scroll', this.update.bind(this), { passive: true });
      this.update();

      // Horizontal overflow-x scrollers capture vertical wheel; pass vertical intent to the page.
      this.onWheel = function (e) {
        if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        window.scrollBy({ top: e.deltaY, behavior: 'auto' });
        e.preventDefault();
      };
      this.addEventListener('wheel', this.onWheel, { passive: false, capture: true });

      // Opt-in auto-advance (data-autoplay). Pauses on hover/focus, respects reduced motion.
      // Skip on mobile for feature-rows (manual arrows) and listing pages (static stack).
      var isMobile = window.matchMedia('(max-width: 749px)').matches;
      var skipAutoplayOnMobile = isMobile && (
        this.classList.contains('beam-feature-rows__carousel')
        || document.body.classList.contains('template-collection')
        || document.body.classList.contains('template-search')
      );
      if (this.hasAttribute('data-autoplay') && !reduceMotion && !skipAutoplayOnMobile) {
        this.interval = parseInt(this.getAttribute('data-autoplay-interval'), 10) || 5000;
        this.paused = false;
        var pause = function () { this.paused = true; }.bind(this);
        var resume = function () { this.paused = false; }.bind(this);
        this.addEventListener('mouseenter', pause);
        this.addEventListener('mouseleave', resume);
        this.addEventListener('focusin', pause);
        this.addEventListener('focusout', resume);
        this.autoplayTimer = setInterval(this.autoplayTick.bind(this), this.interval);
      }
    }
    goTo(i) {
      var card = this.cards[i];
      if (!card || !this.scroller) return;
      var delta = card.getBoundingClientRect().left - this.scroller.getBoundingClientRect().left;
      this.scroller.scrollBy({ left: delta, behavior: 'smooth' });
    }
    autoplayTick() {
      if (this.paused) return;
      var s = this.scroller;
      if (s.scrollLeft > s.scrollWidth - s.clientWidth - 8) {
        s.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        s.scrollBy({ left: this.step(), behavior: 'smooth' });
      }
    }
    disconnectedCallback() {
      if (this.onWheel) this.removeEventListener('wheel', this.onWheel, { capture: true });
      if (this.autoplayTimer) clearInterval(this.autoplayTimer);
    }
    update() {
      var s = this.scroller;
      if (this.prev && this.next) {
        this.prev.disabled = s.scrollLeft < 8;
        this.next.disabled = s.scrollLeft > s.scrollWidth - s.clientWidth - 8;
      }
      if (!this.dots.length || !this.cards.length) return;
      var scrollMid = s.scrollLeft + s.clientWidth / 2;
      var best = 0;
      var bestDist = Infinity;
      this.cards.forEach(function (card, i) {
        var center = card.offsetLeft + card.offsetWidth / 2;
        var dist = Math.abs(center - scrollMid);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      this.dots.forEach(function (dot, i) {
        var on = i === best;
        dot.setAttribute('aria-current', on ? 'true' : 'false');
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
  });

  /* ---------- PDP product gallery (thumbs + slide sync) ---------- */
  define('beam-gallery', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-thumb]'));
      this.thumbStrip = this.querySelector('[data-thumbs]');
      var self = this;
      this.thumbs.forEach(function (t) {
        t.addEventListener('click', function () { self.goTo(parseInt(t.dataset.thumb, 10)); });
      });
      if (this.track && this.slides.length) {
        this.onTrackScroll = function () { self.syncFromScroll(); };
        this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
        this.syncFromScroll();
      }
      if ('IntersectionObserver' in window && this.track && this.slides.length) {
        this.io = new IntersectionObserver(function (entries) {
          var best = null;
          entries.forEach(function (en) {
            if (en.isIntersecting && (!best || en.intersectionRatio > best.intersectionRatio)) best = en;
          });
          if (best) self.setActive(self.slides.indexOf(best.target));
        }, { root: this.track, threshold: [0.35, 0.55, 0.75] });
        this.slides.forEach(function (s) { self.io.observe(s); });
      }
      this.onVariantMedia = function (e) { self.goToMedia(e.detail.media); };
      document.addEventListener('beam:variant-media', this.onVariantMedia);
      // Horizontal scroller captures wheel by default; only handle horizontal intent.
      this.onWheel = function (e) {
        if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        window.scrollBy({ top: e.deltaY, behavior: 'auto' });
        e.preventDefault();
      };
      this.addEventListener('wheel', this.onWheel, { passive: false, capture: true });
    }
    disconnectedCallback() {
      if (this.onWheel) this.removeEventListener('wheel', this.onWheel, { capture: true });
      if (this.onTrackScroll && this.track) this.track.removeEventListener('scroll', this.onTrackScroll);
      if (this.onVariantMedia) document.removeEventListener('beam:variant-media', this.onVariantMedia);
      if (this.io) this.io.disconnect();
    }
    syncFromScroll() {
      if (!this.track || !this.slides.length) return;
      var mid = this.track.scrollLeft + this.track.clientWidth / 2;
      var best = 0;
      var bestDist = Infinity;
      this.slides.forEach(function (slide, i) {
        var center = slide.offsetLeft + slide.offsetWidth / 2;
        var dist = Math.abs(center - mid);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      this.setActive(best);
    }
    goTo(i, instant) {
      var s = this.slides[i];
      if (!s || !this.track) return;
      var delta = s.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
      if (instant || reduceMotion) this.track.scrollLeft += delta;
      else this.track.scrollBy({ left: delta, behavior: 'smooth' });
      this.setActive(i);
    }
    goToMedia(id) {
      var s = this.slides.find(function (x) { return x.dataset.mediaId === String(id); });
      if (s) this.goTo(this.slides.indexOf(s), true);
    }
    setActive(i) {
      if (this.activeIndex === i) return;
      this.activeIndex = i;
      this.thumbs.forEach(function (t, idx) {
        t.classList.toggle('is-active', idx === i);
        t.setAttribute('aria-current', idx === i ? 'true' : 'false');
      });
      var thumb = this.thumbs[i];
      if (thumb && this.thumbStrip) {
        var strip = this.thumbStrip;
        var left = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
        strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    }
  });

  /* ---------- Feature switcher (click a title to cross-fade its media) ---------- */
  define('beam-switcher', class extends HTMLElement {
    connectedCallback() {
      this.titles = Array.prototype.slice.call(this.querySelectorAll('[data-switch-title]'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-switch-panel]'));
      if (!this.titles.length) return;
      this.titles.forEach(function (t) {
        t.addEventListener('click', function () { this.activate(t.getAttribute('data-switch-title')); }.bind(this));
      }.bind(this));
    }
    activate(idx) {
      this.titles.forEach(function (t) {
        var on = t.getAttribute('data-switch-title') === idx;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.panels.forEach(function (p) {
        p.classList.toggle('is-active', p.getAttribute('data-switch-panel') === idx);
      });
    }
  });

  /* ---------- Count-up stat counters ---------- */
  define('beam-count-up', class extends HTMLElement {
    connectedCallback() {
      var target = parseFloat(this.dataset.to || this.textContent.replace(/[^0-9.]/g, '')) || 0;
      if (reduceMotion || !('IntersectionObserver' in window)) return;
      var numberEl = this.querySelector('[data-count-number]') || this;
      // Prefix/suffix are rendered as sibling affix spans in the Liquid markup.
      // Only animate the numeric node so we don't double-append ("110++", "600k+k+").
      var hasAffixNodes = !!this.querySelector('.beam-stat-counters__affix');
      var suffix = hasAffixNodes ? '' : (this.dataset.suffix || '');
      var prefix = hasAffixNodes ? '' : (this.dataset.prefix || '');
      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          obs.disconnect();
          var start = null, dur = 1400;
          var tick = function (t) {
            if (!start) start = t;
            var p = Math.min((t - start) / dur, 1);
            var eased = 1 - Math.pow(1 - p, 3);
            var val = target * eased;
            numberEl.textContent = prefix + (target % 1 === 0 ? Math.round(val) : val.toFixed(1)) + suffix;
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        });
      }, { threshold: 0.4 });
      obs.observe(this);
    }
  });

  /* ---------- Sticky anchor nav (PDP scrollspy + in-page jump) ---------- */
  define('beam-anchor-nav', class extends HTMLElement {
    connectedCallback() {
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);
      var start = this.start.bind(this);
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
      else start();
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }
    resolveTarget(id) {
      var aliases = {
        'pdp-overview': ['pdp-overview', 'features'],
        'pdp-spec': ['pdp-spec', 'specification'],
        'pdp-package': ['pdp-package', 'package'],
        'specification': ['specification', 'pdp-spec'],
        'package': ['package', 'pdp-package'],
        'features': ['features', 'pdp-overview']
      };
      var ids = aliases[id] || [id];
      for (var i = 0; i < ids.length; i++) {
        var el = document.getElementById(ids[i]);
        if (el) return el;
      }
      return null;
    }
    start() {
      Array.prototype.slice.call(this.querySelectorAll('a[href^="#"]')).forEach(function (l) {
        var id = (l.getAttribute('href') || '').slice(1);
        if (!id || this.resolveTarget(id)) return;
        var item = l.closest('li');
        if (item) item.remove();
        else l.remove();
      }, this);
      this.links = Array.prototype.slice.call(this.querySelectorAll('a[href^="#"]'));
      if (!this.links.length) {
        this.hidden = true;
        return;
      }
      var targets = this.links.map(function (l) {
        return this.resolveTarget((l.getAttribute('href') || '').slice(1));
      }, this).filter(Boolean);
      if (!targets.length || !('IntersectionObserver' in window)) return;
      var self = this;
      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          self.links.forEach(function (l) {
            if (self.resolveTarget((l.getAttribute('href') || '').slice(1)) === e.target) {
              l.setAttribute('aria-current', 'true');
            } else {
              l.removeAttribute('aria-current');
            }
          });
        });
      }, { rootMargin: '-45% 0px -50% 0px' });
      targets.forEach(function (t) { obs.observe(t); });
    }
    onClick(e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a || a.getAttribute('href') === '#') return;
      var id = a.getAttribute('href').slice(1);
      var target = this.resolveTarget(id);
      if (!target) return;
      e.preventDefault();
      var section = target.closest('.shopify-section') || target;
      var header = document.querySelector('[data-beam-header]');
      var offset = this.getBoundingClientRect().height;
      if (header) offset += header.getBoundingClientRect().height;
      var top = section.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
      if (history.replaceState) history.replaceState(null, '', '#' + id);
    }
  });

  /* ---------- Parallax / scroll-scrubbed video ---------- */
  define('beam-parallax-video', class extends HTMLElement {
    connectedCallback() {
      if (reduceMotion) return;
      this.video = this.querySelector('video');
      this.caps = Array.prototype.slice.call(this.querySelectorAll('[data-reveal-caption]'));
      this.scrub = this.querySelector('[data-scrub-fill]');
      if (this.video) { this.video.pause(); this.video.removeAttribute('autoplay'); }
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
    onScroll() {
      var rect = this.getBoundingClientRect();
      var vh = window.innerHeight;
      var progress = Math.min(Math.max((vh - rect.top) / (vh + rect.height), 0), 1);
      if (this.video && this.video.duration) {
        this.video.currentTime = this.video.duration * progress;
      }
      if (this.scrub) this.scrub.style.height = (progress * 100) + '%';
      if (this.caps.length) {
        var seg = progress * this.caps.length;
        var active = Math.min(Math.floor(seg), this.caps.length - 1);
        this.caps.forEach(function (c, i) { c.classList.toggle('is-active', i === active); });
        // Mask "breathes": eases up (1.0) as a caption centres in its slot,
        // eases down (0.55) between beats — the dim tracks the storytelling.
        var frac = seg - Math.floor(seg);
        var mask = 0.55 + 0.45 * Math.sin(frac * Math.PI);
        this.style.setProperty('--beam-pv-mask', mask.toFixed(3));
      }
    }
  });

  /* ---------- Lazy external-video facade (YouTube / Vimeo) ---------- */
  define('video-facade', class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.load.bind(this), { once: true });
    }
    load() {
      var src = this.dataset.src || '';
      var host = this.dataset.host;
      var embed = src;
      var id;
      if (host === 'youtube' || /youtu/.test(src)) {
        id = (src.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/) || [])[1];
        if (id) embed = 'https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0';
      } else if (host === 'vimeo' || /vimeo/.test(src)) {
        id = (src.match(/vimeo\.com\/(?:video\/)?(\d+)/) || [])[1];
        if (id) embed = 'https://player.vimeo.com/video/' + id + '?autoplay=1';
      }
      var iframe = document.createElement('iframe');
      iframe.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('title', 'Video');
      iframe.loading = 'lazy';
      iframe.src = embed;
      iframe.style.cssText = 'width:100%;height:100%;border:0;position:absolute;inset:0';
      this.style.position = 'relative';
      this.innerHTML = '';
      this.appendChild(iframe);
    }
  });

  /* ---------- Predictive-search input ---------- */
  define('beam-predictive-search', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results || this.dataset.enabled !== 'true') return;
      var t;
      this.input.addEventListener('input', function () {
        clearTimeout(t);
        var q = this.input.value.trim();
        if (q.length < 2) { this.results.innerHTML = ''; this.results.hidden = true; return; }
        t = setTimeout(this.search.bind(this, q), 220);
      }.bind(this));
    }
    search(q) {
      var url = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') +
        'search/suggest?q=' + encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=6&section_id=beam-predictive-search';
      fetch(url).then(function (r) { return r.text(); }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var inner = doc.querySelector('[data-predictive-results]');
        this.results.innerHTML = inner ? inner.innerHTML : '';
        this.results.hidden = false;
      }.bind(this)).catch(function () {});
    }
  });

  /* ---------- Mobile menu drill-down (panel stack + back) ---------- */
  define('beam-menu-drawer', class extends HTMLElement {
    connectedCallback() {
      this.stack = ['root'];
      this.backBtn = this.querySelector('[data-menu-back]');
      this.titleEl = this.querySelector('[data-menu-title]');
      this.onClick = this.onClick.bind(this);
      this.reset = this.reset.bind(this);
      this.addEventListener('click', this.onClick);
      var overlay = this.closest('beam-overlay');
      if (overlay) overlay.addEventListener('beam:overlay:close', this.reset);
      this.render();
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      var overlay = this.closest('beam-overlay');
      if (overlay) overlay.removeEventListener('beam:overlay:close', this.reset);
    }
    onClick(e) {
      var open = e.target.closest('[data-open-panel]');
      if (open) {
        e.preventDefault();
        this.stack.push(open.getAttribute('data-open-panel'));
        this.render();
        return;
      }
      if (e.target.closest('[data-menu-back]')) {
        e.preventDefault();
        if (this.stack.length > 1) this.stack.pop();
        this.render();
      }
    }
    reset() {
      this.stack = ['root'];
      this.render();
    }
    render() {
      var id = this.stack[this.stack.length - 1];
      var prev = this.stack.length > 1 ? this.stack[this.stack.length - 2] : null;
      var active = null;
      var panels = this.querySelectorAll('[data-panel]');
      for (var i = 0; i < panels.length; i++) {
        var p = panels[i];
        var pid = p.getAttribute('data-panel');
        p.removeAttribute('hidden');
        p.classList.toggle('is-active', pid === id);
        p.classList.toggle('is-left', pid === prev);
        if (pid === id) active = p;
      }
      if (active) active.scrollTop = 0;
      if (this.titleEl) {
        this.titleEl.textContent = (active && active.getAttribute('data-panel-title')) || this.titleEl.textContent;
      }
      if (this.backBtn) {
        if (this.stack.length > 1) this.backBtn.removeAttribute('hidden');
        else this.backBtn.setAttribute('hidden', '');
      }
    }
  });

  /* ---------- Global overlay controller (drawers + modals) ---------- */
  define('beam-overlay', class extends HTMLElement {
    connectedCallback() {
      this.id = this.getAttribute('id');
      this.dialog = this.querySelector('[data-overlay-panel]');
      document.addEventListener('click', function (e) {
        var opener = e.target.closest('[data-overlay-open="' + this.id + '"]');
        if (opener) { e.preventDefault(); this.open(opener); }
      }.bind(this));
      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-overlay-close]') || e.target === this) this.close();
      }.bind(this));
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && this.hasAttribute('open')) this.close(); }.bind(this));
    }
    open(trigger) {
      this.opener = trigger;
      this.setAttribute('open', '');
      document.documentElement.classList.add('beam-no-scroll');
      var focusable = this.querySelector('[data-overlay-panel] a, [data-overlay-panel] button, [data-overlay-panel] input');
      if (focusable) focusable.focus();
      this.dispatchEvent(new CustomEvent('beam:overlay:open', { bubbles: true }));
    }
    close() {
      this.removeAttribute('open');
      document.documentElement.classList.remove('beam-no-scroll');
      if (this.opener) this.opener.focus();
      this.dispatchEvent(new CustomEvent('beam:overlay:close', { bubbles: true }));
    }
  });

  /* ---------- Cart: add-to-cart + drawer refresh ---------- */
  var Beam = window.Beam = window.Beam || {};
  Beam.routes = (window.Shopify && window.Shopify.routes) || { root: '/' };

  Beam.fetchCart = function () {
    return fetch(Beam.routes.root + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  };

  Beam.updateCartUI = function (cart) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
      var cartLink = el.closest('a.beam-header__cart');
      if (cartLink) {
        var base = (cartLink.getAttribute('aria-label') || 'Cart').replace(/\s*\(\d+\)\s*$/, '').trim();
        cartLink.setAttribute('aria-label', cart.item_count > 0 ? base + ' (' + cart.item_count + ')' : base);
      }
    });
    document.dispatchEvent(new CustomEvent('beam:cart:updated', { detail: cart }));
  };

  Beam.refreshDrawer = function () {
    var drawer = document.getElementById('beam-cart-drawer');
    if (!drawer) return Beam.fetchCart().then(Beam.updateCartUI);
    return fetch(Beam.routes.root + '?section_id=beam-cart-drawer')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById('beam-cart-drawer');
        if (fresh) drawer.innerHTML = fresh.innerHTML;
        return Beam.fetchCart().then(Beam.updateCartUI);
      });
  };

  function cartAddPayload(form) {
    var idInput = form.querySelector('[name="id"]');
    if (!idInput || !idInput.value) return null;
    var qtyInput = form.querySelector('[name="quantity"]');
    return {
      items: [{
        id: parseInt(idInput.value, 10),
        quantity: parseInt(qtyInput && qtyInput.value ? qtyInput.value : '1', 10) || 1
      }]
    };
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    var payload = cartAddPayload(form);
    if (!payload) return;
    var button = e.submitter || form.querySelector('[type="submit"]');
    if (button) { button.setAttribute('aria-disabled', 'true'); button.classList.add('is-loading'); }
    fetch(Beam.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) {
        return r.text().then(function (text) {
          var item;
          try { item = text ? JSON.parse(text) : {}; }
          catch (err) { throw { description: text || 'Could not add to cart.' }; }
          if (!r.ok || item.status) throw item;
          return item;
        });
      })
      .then(function (item) {
        if (button && button.hasAttribute('data-checkout-now')) {
          window.location.href = Beam.routes.root + 'checkout';
          return;
        }
        return Beam.refreshDrawer().then(function () {
          var drawer = document.getElementById('beam-cart-drawer-overlay');
          if (drawer && drawer.open) drawer.open(button);
          document.dispatchEvent(new CustomEvent('beam:cart:added', { detail: item }));
        });
      })
      .catch(function (err) {
        var msg = form.querySelector('[data-form-error]');
        if (msg) msg.textContent = (err && (err.description || err.message)) || 'Could not add to cart.';
      })
      .finally(function () {
        if (button) { button.removeAttribute('aria-disabled'); button.classList.remove('is-loading'); }
      });
  });

  /* Cart line quantity changes inside the drawer */
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cart-quantity]');
    if (!input) return;
    fetch(Beam.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: input.dataset.lineKey, quantity: parseInt(input.value, 10) })
    })
      .then(function (r) { return r.json(); })
      .then(function () { Beam.refreshDrawer(); });
  });
})();
